/**
 * Sibling ordering.
 *
 * `Node.order` is a *fractional index* — a string that sorts lexicographically — not an integer
 * position. The difference matters because Orbit Mind is offline-first:
 *
 *   With integers, inserting between two siblings renumbers everything after it. Two devices
 *   editing offline both renumber, and the merge has to choose a loser. Reordering one node
 *   turns into a conflict over every node in the branch.
 *
 *   With a fractional index, inserting between two siblings mints a new key between their two
 *   keys and touches nothing else. Two devices inserting at the same spot produce different keys
 *   and both survive the merge, in a stable order.
 *
 * The generation algorithm is `fractional-indexing` (rocicorp, zero dependencies). It is wrapped
 * here rather than imported directly at call sites: ordering is a domain rule, and the day the
 * implementation changes there should be one file to change.
 */
import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing';
/** The key for the first child of an empty parent. */
export function firstOrder() {
    return generateKeyBetween(null, null);
}
/**
 * Rejects bounds that are not in ascending order.
 *
 * `generateKeyBetween` throws when the bounds are equal but NOT when they are inverted: given
 * ("a1", "a0") it returns "a0V", a key below the lower bound and above the upper one. A caller
 * that swapped its arguments would silently place a node somewhere the user never asked for, and
 * nothing downstream could tell that had happened. Catching it here is the reason this module
 * wraps the library rather than re-exporting it.
 */
function assertOrdered(before, after) {
    if (before !== null && after !== null && before >= after) {
        throw new RangeError(`order bounds are not ascending: "${before}" >= "${after}"`);
    }
}
/**
 * A key strictly between `before` and `after`.
 *
 * `null` means "no bound": `between(null, x)` goes before everything, `between(x, null)` after.
 * Throws when the bounds are out of order — that is a caller bug, not a recoverable state.
 */
export function between(before, after) {
    assertOrdered(before, after);
    return generateKeyBetween(before, after);
}
/** `count` evenly spaced keys strictly between the bounds, for inserting a run of nodes at once. */
export function betweenMany(before, after, count) {
    if (count < 0)
        throw new RangeError(`count must not be negative: ${String(count)}`);
    assertOrdered(before, after);
    if (count === 0)
        return [];
    return generateNKeysBetween(before, after, count);
}
/** Comparator for sorting siblings. Plain string comparison is the whole point of the scheme. */
export function compareOrder(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/**
 * Key that places a node at the end of `siblings`.
 *
 * Takes the keys rather than the nodes so it stays usable during a move, when the node being
 * placed is not yet in the list.
 */
export function orderAtEnd(siblingOrders) {
    if (siblingOrders.length === 0)
        return firstOrder();
    const sorted = [...siblingOrders].sort(compareOrder);
    return between(sorted[sorted.length - 1] ?? null, null);
}
/** Key that places a node at the start of `siblings`. */
export function orderAtStart(siblingOrders) {
    if (siblingOrders.length === 0)
        return firstOrder();
    const sorted = [...siblingOrders].sort(compareOrder);
    return between(null, sorted[0] ?? null);
}
/**
 * Key that places a node at `index` among `siblings`.
 *
 * `index` counts positions in the list as it will look *after* the insert, so index 0 is first
 * and `siblings.length` is last. Out-of-range values clamp rather than throw: a drag that ends
 * slightly past the end of a list means "put it last", not "fail".
 */
export function orderAtIndex(siblingOrders, index) {
    const sorted = [...siblingOrders].sort(compareOrder);
    const at = Math.max(0, Math.min(sorted.length, Math.trunc(index)));
    return between(at === 0 ? null : (sorted[at - 1] ?? null), sorted[at] ?? null);
}
//# sourceMappingURL=ordering.js.map